<template>
    <div>
        <Form @submit.prevent="submitForm">
            <form-item
                :class="{
                    'ivu-form-item-error': showValidationErrors && validationErrors.columnName
                }"
                label="Column name"
            >
                <Input
                    ref="columnNameInput"
                    v-model="internalFormData.columnName"
                    placeholder="e.g. audienceId"
                    @keydown.stop
                />
                <p
                    v-if="showValidationErrors && validationErrors.columnName"
                    class="metadata-field-form__form-error-message"
                >
                    {{ validationErrors.columnName }}
                </p>
                <p class="metadata-field-form__instructions">The column name can't contain spaces</p>
                <p class="metadata-field-form__instructions">Use only letters and numbers</p>
            </form-item>
        </Form>
        <div
            v-if="!hasValidationErrors && hasErrorSubmittingForm"
            :class="{
                'metadata-field-form__error-message-wrapper--top-spacing': !hasValidationErrors
            }"
        >
            <Alert banner type="error">
                <template #desc>
                    There was an unexpected error and no changes were saved. Hopefully it was a temporary issue and
                    should work if you try again in a few moments.
                </template>
            </Alert>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import ValidationError from "hoxton-shared/errors/ValidationError";
import isValidMetadataFieldName from "hoxton-shared/utils/isValidMetadataFieldName";

function processErrorMessage(err: Error | ValidationError) {
    if (!err.message) {
        return "";
    }

    // Handle the error coming from the API or being thrown locally. The code running locally mangles the class name so we need to check its type instead.
    if (
        err instanceof ValidationError ||
        ((err as ValidationError).extensions && (err as ValidationError).extensions.type === "ValidationError")
    ) {
        return err.message.replace("GraphQL error: ", "");
    }

    return "";
}

export default defineComponent({
    props: {
        hasErrorSubmittingForm: {
            type: Boolean
        },

        initialFormData: {
            default: () => ({}),
            type: Object as PropType<{ columnName: string }>
        },

        showValidationErrors: {
            type: Boolean
        },

        error: {
            default: () => null,
            type: Object as PropType<Error | null>
        }
    },

    emits: ["formDataUpdate", "hasValidationErrorsUpdate", "submit"],

    data() {
        return {
            internalFormData: {
                columnName: this.initialFormData.columnName
            }
        };
    },
    computed: {
        hasValidationErrors() {
            return Object.keys(this.validationErrors).length > 0;
        },

        validationErrors() {
            const validationErrors = {} as {
                columnName?: string;
            };

            if (this.error) {
                const processedErrorMessage = processErrorMessage(this.error);
                if (processedErrorMessage) {
                    validationErrors.columnName = processedErrorMessage;

                    return validationErrors;
                }
            }

            if (!this.internalFormData.columnName) {
                validationErrors.columnName = "A column name is required";
            } else {
                try {
                    isValidMetadataFieldName(this.internalFormData.columnName);
                } catch (err) {
                    // The assert module on the front-end is not throwing the custom error but instead throws an AssertionError with the custom error set as the message property
                    // which is why we pass down the message property here and not the err variable. This is likely a symptom of jest overriding the assert module
                    // Refs: https://github.com/facebook/jest/issues/7547, https://github.com/vuejs/vue-cli/issues/1131
                    validationErrors.columnName = processErrorMessage(err as Error | ValidationError);
                }
            }

            return validationErrors;
        }
    },

    watch: {
        internalFormData: {
            deep: true,
            handler() {
                this.$emit("formDataUpdate", { ...this.internalFormData });
            },
            immediate: true
        },

        hasValidationErrors: {
            handler() {
                this.$emit("hasValidationErrorsUpdate", this.hasValidationErrors);
            },
            immediate: true
        }
    },

    async mounted() {
        await this.$nextTick();
        (this.$refs.columnNameInput as HTMLInputElement).focus();
    },

    methods: {
        submitForm() {
            this.$emit("submit", { ...this.internalFormData });
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.metadata-field-form__error-message-wrapper--top-spacing {
    padding: $spacing 0 0;
}

.metadata-field-form__form-error-message {
    color: $error-color;
    font-size: $font-size-small;
    margin: $spacing-small 0 $spacing-small;
    line-height: $font-size-large;
}

.metadata-field-form__instructions {
    color: $grey5;
    font-size: $font-size-small;
    margin: $spacing-small 0 0;
    line-height: $font-size-small;
    clear: both;
}
</style>
